import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { FamilyTree } from "@/components/FamilyTree";
import { TreeErrorBoundary } from "@/components/TreeErrorBoundary";
import { GlobeView } from "@/components/GlobeView";
import { StoryView } from "@/components/StoryView";
import { TimelineView } from "@/components/TimelineView";
import { AddMemberDialog } from "@/components/AddMemberDialog";
import { SuggestPersonDialog } from "@/components/SuggestPersonDialog";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { ImportDialog } from "@/components/ImportDialog";
import { DuplicatesDialog } from "@/components/DuplicatesDialog";
import { LoginDialog } from "@/components/LoginDialog";
import { ManageUsersDialog } from "@/components/ManageUsersDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { InviteAcceptView } from "@/components/InviteAcceptView";
import { ResetPasswordView } from "@/components/ResetPasswordView";
import { ContributeDialog } from "@/components/ContributeDialog";
import { BulkPhotoImportDialog } from "@/components/BulkPhotoImportDialog";
import { PhotoWall } from "@/components/PhotoWall";
import { SupportDialog } from "@/components/SupportDialog";
import { AssistantDialog } from "@/components/AssistantDialog";
import { LandingPage } from "@/components/LandingPage";
import { PublicTreeView } from "@/components/PublicTreeView";
import { FamilyShareDialog } from "@/components/FamilyShareDialog";
import { MemberSheet } from "@/components/MemberSheet";
import { PersonCard } from "@/components/PersonCard";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { photoUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TreePine, Plus, Sparkles, LayoutGrid, List, Clapperboard, Globe2, Upload, Merge, LogIn, LogOut, Shield, KeyRound, ChevronDown, History, Share2, Images, HeartHandshake, GalleryHorizontalEnd, Heart, Home, User, MoreHorizontal, Wrench } from "lucide-react";
import { kinshipLabel } from "@/lib/layout";
import { TreeSkeleton, ListSkeleton, GallerySkeleton, SkeletonBlock } from "@/components/LoadingSkeletons";

const HERO = "https://images.unsplash.com/photo-1609260840244-22b52bfc6664?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGJsYWNrJTIwZmFtaWx5JTIwbXVsdGlwbGUlMjBnZW5lcmF0aW9ucyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4OTM1OTMwMnww&ixlib=rb-4.1.0&q=85";

function years(m) {
  const b = m.birth_date ? String(m.birth_date).slice(0, 4) : "";
  const d = m.death_date ? String(m.death_date).slice(0, 4) : "";
  return b || d ? `${b || "?"}${d ? " – " + d : ""}` : "—";
}

// Persistent "who am I looking at" strip — name, photo and relationship to the home person.
function SelectedContextStrip({ member, members, homeId, canEdit, onSetHome, onOpen }) {
  if (!member) return null;
  const isHome = member.id === homeId;
  const rel = homeId && homeId !== member.id ? kinshipLabel(members, homeId, member.id) : null;
  const relText = isHome ? "This is you" : (homeId ? (rel ? `your ${rel}` : "a relative of yours") : "Tap a person to set your viewpoint");
  return (
    <div data-testid="selected-context-strip" className="pointer-events-auto absolute bottom-5 left-5 z-40 flex max-w-[calc(100%-6.5rem)] items-center gap-3 rounded-2xl border border-[#D8E0E8] bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
      <span className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-[#E8EEF3] ring-2 ring-[#D4A017]/50">
        {photoUrl(member.photo_path) ? <img src={photoUrl(member.photo_path)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-[#94A3B8]"><User size={18} /></span>}
      </span>
      <div className="min-w-0">
        <button onClick={() => onOpen(member.id)} data-testid="context-strip-name" className="block max-w-[200px] truncate text-left font-serif text-sm font-bold text-[#082D4A] hover:underline">{member.full_name}</button>
        <div className="truncate text-xs font-semibold text-[#D4A017]" data-testid="context-strip-rel">{relText}</div>
      </div>
      {canEdit && (
        isHome ? (
          <button onClick={() => onSetHome(null)} data-testid="clear-home-button" className="ml-1 shrink-0 rounded-full border border-[#D8E0E8] px-2.5 py-1 text-xs font-medium text-[#64748B] hover:bg-[#E8EEF3]">Not me</button>
        ) : (
          <button onClick={() => onSetHome(member.id)} data-testid="set-home-button" className="ml-1 flex shrink-0 items-center gap-1 rounded-full border border-[#D8E0E8] px-2.5 py-1 text-xs font-medium text-[#082D4A] hover:bg-[#E8EEF3]"><Home size={12} /> This is me</button>
        )
      )}
    </div>
  );
}

export default function App() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [groups, setGroups] = useState([]);
  const [frames, setFrames] = useState([]);
  const [view, setView] = useState("tree");
  const [selectedId, setSelectedId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [addCtx, setAddCtx] = useState({ open: false, relateTo: null, relation: null, relateToName: null });
  const [importOpen, setImportOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [storyScope, setStoryScope] = useState(null); // {ids, name} | null
  const [loginOpen, setLoginOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [bulkPhotoOpen, setBulkPhotoOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const [family, setFamily] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { isAuthed, isAdmin, user, ready, logout, loginWithToken } = useAuth();
  const canEditLive = isAuthed && (user?.role === "admin" || user?.role === "member");
  const canSuggest = isAuthed && user?.role === "contributor";

  // Emergent Google OAuth callback: exchange #session_id for our JWT, then clean the URL.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleProcessed = useRef(false);
  useEffect(() => {
    if (googleProcessed.current) return;
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    googleProcessed.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    if (!sid) return;
    api.googleSession(sid)
      .then(({ token, user: u }) => {
        loginWithToken(token, u);
        toast.success(`Welcome, ${u.name?.split(" ")[0] || "friend"}!`);
      })
      .catch(() => toast.error("Google sign-in failed. Please try again."))
      .finally(() => {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      });
  }, [loginWithToken]);
  const inviteToken = (typeof window !== "undefined" && window.location.pathname.match(/^\/invite\/([^/]+)/)?.[1]) || null;
  const publicSlug = (typeof window !== "undefined" && window.location.pathname.match(/^\/family\/([^/]+)/)?.[1]) || null;
  const isResetPage = typeof window !== "undefined" && window.location.pathname.startsWith("/reset-password");
  const resetToken = isResetPage ? new URLSearchParams(window.location.search).get("token") : null;

  const guarded = (fn) => () => (isAuthed ? fn() : setLoginOpen(true));

  const descendantsOf = useCallback((rootId) => {
    const ids = [rootId];
    const queue = [rootId];
    while (queue.length) {
      const cur = queue.shift();
      members.forEach((m) => {
        if ((m.parent_ids || []).includes(cur) && !ids.includes(m.id)) {
          ids.push(m.id);
          queue.push(m.id);
        }
      });
    }
    return ids;
  }, [members]);

  const playBranchStory = (member) => {
    setStoryScope({ ids: descendantsOf(member.id), name: member.full_name });
    setSheetOpen(false);
    setView("video");
  };

  const load = useCallback(async () => {
    try {
      const data = await api.list();
      setMembers(data);
    } finally {
      setLoading(false);
    }
  }, []);
  const loadSettings = useCallback(async () => {
    try { setSettings(await api.getSettings()); } catch (e) { console.error("Failed to load settings", e); }
  }, []);

  const loadGroups = useCallback(async () => {
    try { setGroups((await api.listGroups()).groups || []); } catch (e) { console.error("Failed to load groups", e); }
  }, []);

  const loadFrames = useCallback(async () => {
    try { setFrames((await api.listFrames()).frames || []); } catch (e) { console.error("Failed to load frames", e); }
  }, []);

  const loadFamily = useCallback(async () => {
    try { setFamily(await api.getFamily()); } catch (e) { console.error("Failed to load family", e); }
  }, []);

  const [pendingRequests, setPendingRequests] = useState(0);
  const loadPending = useCallback(async () => {
    if (!isAdmin) { setPendingRequests(0); return; }
    try { setPendingRequests((await api.listContribRequests()).pending || 0); } catch (e) { console.error("Failed to load pending requests", e); }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAuthed) { setLoading(false); return; }
    setLoading(true);
    load();
    loadSettings();
    loadGroups();
    loadFrames();
    loadFamily();
  }, [isAuthed, load, loadSettings, loadGroups, loadFrames, loadFamily]);

  useEffect(() => {
    if (!isAuthed) return;
    (async () => {
      try {
        const { updates } = await api.getMyUpdates();
        if (updates?.length) {
          const verb = { add: "addition", edit: "edit", delete: "removal" };
          updates.slice(0, 4).forEach((u) =>
            toast.success(`🎉 Your ${verb[u.type] || "suggestion"} of ${u.member_name || "a relative"} is now live — thank you!`, { duration: 6000 })
          );
          await api.ackMyUpdates();
        }
      } catch (e) { console.error("Failed to fetch my updates", e); }
    })();
  }, [isAuthed]);

  useEffect(() => {
    loadPending();
    if (!isAdmin) return;
    const t = setInterval(loadPending, 60000);
    return () => clearInterval(t);
  }, [isAdmin, loadPending]);

  // Deep-link: open a shared person (?person=<id>) once the tree has loaded.
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (loading || deepLinkDone.current || !members.length) return;
    deepLinkDone.current = true;
    const pid = new URLSearchParams(window.location.search).get("person");
    if (pid && members.some((m) => m.id === pid)) {
      setView("tree");
      setSelectedId(pid);
      setSheetOpen(true);
    }
  }, [loading, members]);

  const shareTree = () => setShareOpen(true);

  const bgUrl = settings?.tree_bg_path ? photoUrl(settings.tree_bg_path) : null;
  const homeId = settings?.home_member_id || null;
  const setHome = async (id) => {
    try { await api.setHomePerson(id); await loadSettings(); toast.success(id ? "Set as your viewpoint — relationships now show relative to you" : "Cleared your viewpoint"); }
    catch { toast.error("Could not update your viewpoint"); }
  };

  const selected = members.find((m) => m.id === selectedId) || null;

  if (inviteToken) return <InviteAcceptView token={inviteToken} />;
  if (isResetPage) return <ResetPasswordView token={resetToken} />;
  if (publicSlug) return <PublicTreeView slug={publicSlug} />;
  if (!ready) return (
    <div className="min-h-screen bg-[#F4F6F8]" data-testid="app-boot-skeleton">
      <div className="flex items-center justify-between border-b border-[#D8E0E8] bg-white px-5 py-3">
        <div className="flex items-center gap-3">
          <img src="/logo-mark.png" alt="" className="flame-pulse h-10 w-10 object-contain" data-testid="boot-flame" />
          <SkeletonBlock className="h-5 w-44" />
        </div>
        <div className="flex items-center gap-2">
          <SkeletonBlock className="h-9 w-56 !rounded-full" />
          <SkeletonBlock className="h-9 w-28 !rounded-full" />
        </div>
      </div>
      <TreeSkeleton />
    </div>
  );
  if (!isAuthed)
    return (
      <>
        <LandingPage
          onSignUp={() => { setAuthMode("signup"); setLoginOpen(true); }}
          onSignIn={() => { setAuthMode("signin"); setLoginOpen(true); }}
          onDonate={() => setSupportOpen(true)}
        />
        <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} defaultMode={authMode} />
        <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
        <Toaster position="top-center" richColors />
      </>
    );

  const openMember = (id) => {
    setSelectedId(id);
    setSheetOpen(true);
  };

  const openAddRoot = () => setAddCtx({ open: true, relateTo: null, relation: null, relateToName: null });
  const openAddRelative = (member, relation) => {
    setSheetOpen(false);
    const spouseNames = (member.spouse_ids || [])
      .map((id) => members.find((m) => m.id === id)?.full_name)
      .filter(Boolean);
    setAddCtx({ open: true, relateTo: member.id, relation, relateToName: member.full_name, spouseNames });
  };

  const afterChange = async () => {
    await load();
  };

  const views = [
    { id: "tree", label: "Tree", icon: LayoutGrid, testid: "tree-view-tab" },
    { id: "list", label: "List", icon: List, testid: "list-view-tab" },
    { id: "timeline", label: "Timeline", icon: History, testid: "timeline-view-tab" },
    { id: "wall", label: "Photo Wall", icon: GalleryHorizontalEnd, testid: "wall-view-tab" },
    { id: "globe", label: "Globe", icon: Globe2, testid: "globe-view-tab" },
    { id: "video", label: "Story Video", icon: Clapperboard, testid: "video-slideshow-tab" },
  ];

  return (
    <div className="flex h-screen flex-col bg-[#F4F6F8]">
      {/* Header */}
      <header className="z-30 flex flex-wrap items-center justify-between gap-3 border-b border-[#D8E0E8] bg-white/80 px-5 py-3 backdrop-blur">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.png" alt="Where Am I From" className="h-10 w-10 object-contain" data-testid="brand-logo" />
          <div className="leading-none">
            <div className="font-brand text-lg leading-none text-[#082D4A]">Where Am i From<span className="text-[#D4A017]">.net</span></div>
            <div className="mt-1 hidden text-[10px] font-medium uppercase tracking-[0.15em] text-[#64748B] sm:block">Discover the roots of your story</div>
          </div>
          {family?.name && (
            <span data-testid="family-name-pill" className="ml-1 hidden items-center rounded-full bg-[#E8EEF3] px-3 py-1 text-xs font-semibold text-[#082D4A] md:inline-flex">{family.name}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex max-w-full overflow-x-auto rounded-full border border-[#D8E0E8] bg-white p-1">
            {views.map((v) => (
              <button
                key={v.id}
                onClick={() => { setView(v.id); if (v.id === "video") setStoryScope(null); }}
                data-testid={v.testid}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === v.id ? "bg-[#082D4A] text-white" : "text-[#64748B] hover:text-[#082D4A]"
                }`}
              >
                <v.icon size={15} />
                <span className="hidden sm:inline">{v.label}</span>
              </button>
            ))}
          </div>
          <Button onClick={shareTree} data-testid="share-tree-button" variant="outline" className="rounded-full border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]">
            <Share2 size={16} className="sm:mr-1.5" />
            <span className="hidden sm:inline">Share</span>
          </Button>
          {isAuthed && (
            <Button onClick={() => setAssistantOpen(true)} data-testid="assistant-button" variant="outline" title="Ask about your family" className="rounded-full border-[#082D4A]/20 bg-[#082D4A]/5 text-[#082D4A] hover:bg-[#082D4A]/10">
              <Sparkles size={16} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Ask</span>
            </Button>
          )}
          {canEditLive && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="tools-menu-button" variant="outline" className="rounded-full border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]">
                  <Wrench size={16} className="sm:mr-1.5" />
                  <span className="hidden sm:inline">Tools</span>
                  <ChevronDown size={14} className="ml-0.5 hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={guarded(() => setImportOpen(true))} data-testid="import-button">
                  <Upload size={15} className="mr-2" /> Import
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setBulkPhotoOpen(true)} data-testid="bulk-photos-button">
                  <Images size={15} className="mr-2" /> Photos
                </DropdownMenuItem>
                <DropdownMenuItem onClick={guarded(() => setDupOpen(true))} data-testid="duplicates-button">
                  <Merge size={15} className="mr-2" /> Duplicates
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button onClick={guarded(openAddRoot)} data-testid="add-member-button" className="rounded-full bg-[#D4A017] text-white hover:bg-[#B0850F]">
            <Plus size={18} className="sm:mr-1.5" />
            <span className="hidden sm:inline">{canSuggest ? "Suggest Person" : "Add Person"}</span>
          </Button>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <NotificationsPanel onChanged={afterChange} />
              <Button onClick={() => setUsersOpen(true)} data-testid="manage-users-button" variant="outline" className="relative rounded-full border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]">
                <Shield size={16} className="sm:mr-1.5" />
                <span className="hidden sm:inline">Members</span>
                {pendingRequests > 0 && <span data-testid="requests-badge" className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#D4A017] px-1 text-[10px] font-bold text-white">{pendingRequests}</span>}
              </Button>
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="more-menu-button" variant="ghost" size="icon" title="More" className="rounded-full text-[#64748B] hover:bg-[#E8EEF3]">
                <MoreHorizontal size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => setSupportOpen(true)} data-testid="donate-button">
                <Heart size={15} className="mr-2 text-[#D4A017]" /> Donate
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isAuthed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="profile-menu-button" variant="ghost" className="rounded-full text-[#64748B]">
                  <span className="hidden max-w-[120px] truncate sm:inline">{user?.name || user?.username}</span>
                  <ChevronDown size={15} className="sm:ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-menu-item">
                  <KeyRound size={15} className="mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                  <LogOut size={15} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button onClick={() => setContributeOpen(true)} data-testid="ask-contribute-button" variant="outline" className="rounded-full border-[#D4A017]/40 bg-white text-[#D4A017] hover:bg-[#D4A017]/5">
                <HeartHandshake size={16} className="sm:mr-1.5" />
                <span className="hidden sm:inline">Ask to contribute</span>
              </Button>
              <Button onClick={() => setLoginOpen(true)} data-testid="login-button" variant="ghost" className="rounded-full text-[#082D4A]">
                <LogIn size={16} className="sm:mr-1.5" />
                <span className="hidden sm:inline">Sign in</span>
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Body */}
      <main className="relative flex-1 overflow-hidden">
        {loading ? (
          view === "list" ? <div className="mx-auto max-w-3xl p-6"><ListSkeleton /></div>
          : view === "wall" ? <div className="p-6"><GallerySkeleton /></div>
          : <TreeSkeleton />
        ) : members.length === 0 ? (
          <EmptyState onAdd={guarded(openAddRoot)} onImport={guarded(() => setImportOpen(true))} />
        ) : view === "tree" ? (
          <>
            <TreeErrorBoundary>
              <FamilyTree members={members} onSelect={openMember} selectedId={selectedId} onChanged={afterChange} canEdit={canEditLive} isAdmin={isAdmin} bgUrl={bgUrl} onBgChanged={loadSettings} onAddRelative={openAddRelative} lineageColors={settings?.lineage_colors || {}} onSettingsChanged={loadSettings} groups={groups} onGroupsChanged={loadGroups} frames={frames} onFramesChanged={loadFrames} familyName={family?.name || ""} homeId={homeId} onRestoreSelection={setSelectedId} narrationVoice={settings?.narration_voice || "fable"} />
            </TreeErrorBoundary>
            <SelectedContextStrip member={selected} members={members} homeId={homeId} canEdit={canEditLive} onSetHome={setHome} onOpen={openMember} />
          </>
        ) : view === "list" ? (
          <ListView members={members} onSelect={openMember} />
        ) : view === "timeline" ? (
          <TimelineView members={members} onSelect={openMember} />
        ) : view === "wall" ? (
          <PhotoWall members={members} onSelect={openMember} />
        ) : view === "globe" ? (
          <GlobeView members={members} onSelect={openMember} onRefresh={load} />
        ) : (
          <StoryView members={members} scopeIds={storyScope?.ids} scopeName={storyScope?.name} canExport={isAuthed} onRequireLogin={() => setLoginOpen(true)} />
        )}
      </main>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={afterChange} />
      <DuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} onDone={afterChange} />

      {canSuggest ? (
        <SuggestPersonDialog
          open={addCtx.open}
          onOpenChange={(o) => setAddCtx((c) => ({ ...c, open: o }))}
          relateTo={addCtx.relateTo}
          relation={addCtx.relation}
          relateToName={addCtx.relateToName}
          onDone={afterChange}
        />
      ) : (
        <AddMemberDialog
          open={addCtx.open}
          onOpenChange={(o) => setAddCtx((c) => ({ ...c, open: o }))}
          relateTo={addCtx.relateTo}
          relation={addCtx.relation}
          relateToName={addCtx.relateToName}
          spouseNames={addCtx.spouseNames}
          onDone={afterChange}
        />
      )}

      <MemberSheet
        member={selected}
        members={members}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onAddRelative={openAddRelative}
        onChanged={afterChange}
        onSelect={openMember}
        onPlayStory={playBranchStory}
        canEdit={canEditLive}
        canSuggest={canSuggest}
        homeId={homeId}
        onSetHome={setHome}
        onRequireLogin={() => setLoginOpen(true)}
        onRequestContribute={() => setContributeOpen(true)}
      />

      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} defaultMode={authMode} />
      <ManageUsersDialog open={usersOpen} onOpenChange={(o) => { setUsersOpen(o); if (!o) loadPending(); }} family={family} onFamilyChanged={loadFamily} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <ContributeDialog open={contributeOpen} onOpenChange={setContributeOpen} family={family?.slug} />
      <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
      <AssistantDialog open={assistantOpen} onOpenChange={setAssistantOpen} />
      <FamilyShareDialog open={shareOpen} onOpenChange={setShareOpen} family={family} isAdmin={isAdmin} onChanged={loadFamily} />
      <BulkPhotoImportDialog open={bulkPhotoOpen} onOpenChange={setBulkPhotoOpen} members={members} onDone={afterChange} />

      <Toaster position="top-center" richColors />
    </div>
  );
}

function EmptyState({ onAdd, onImport }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="grid max-w-4xl overflow-hidden rounded-3xl border border-[#D8E0E8] bg-white shadow-sm md:grid-cols-2">
        <div className="flex flex-col justify-center p-8 sm:p-12">
          <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#E8EEF3] px-3 py-1 text-xs font-medium uppercase tracking-wider text-[#082D4A]">
            <Sparkles size={13} /> Welcome
          </span>
          <h1 className="font-serif text-4xl font-bold leading-tight text-[#082D4A] sm:text-5xl">
            Start your family tree
          </h1>
          <p className="mt-4 text-base leading-relaxed text-[#475569]">
            Add the people you love, connect them across generations, and watch your family story come to life. Begin with just one person — you can grow it any time.
          </p>
          <Button
            onClick={onAdd}
            data-testid="empty-add-button"
            className="mt-6 w-fit rounded-full bg-[#082D4A] px-6 py-6 text-base text-white hover:bg-[#08223A]"
          >
            <Plus size={20} className="mr-2" /> Add the first person
          </Button>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={onAdd} data-testid="empty-add-yourself" className="inline-flex items-center gap-1.5 rounded-full border border-[#D8E0E8] px-4 py-2 text-sm font-medium text-[#082D4A] transition-colors hover:bg-[#E8EEF3]">
              <User size={15} /> Add yourself
            </button>
            <button onClick={onImport} data-testid="empty-import" className="inline-flex items-center gap-1.5 rounded-full border border-[#D8E0E8] px-4 py-2 text-sm font-medium text-[#082D4A] transition-colors hover:bg-[#E8EEF3]">
              <Upload size={15} /> Import a family list
            </button>
          </div>
        </div>
        <div className="relative min-h-[260px]">
          <img src={HERO} alt="Family memories" className="absolute inset-0 h-full w-full object-cover" />
        </div>
      </div>
    </div>
  );
}

function ListView({ members, onSelect }) {
  return (
    <div className="h-full overflow-y-auto p-5 sm:p-8">
      <div className="mx-auto max-w-5xl">
        <h2 className="mb-5 font-serif text-2xl font-semibold text-[#082D4A]">
          Everyone in the family <span className="text-[#64748B]">({members.length})</span>
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {members.map((m) => (
            <div key={m.id} className="flex justify-center">
              <PersonCard member={m} onClick={onSelect} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
